import { QUICK_NOTE_MAX_LENGTH, type QuickNoteDto } from '@bjj/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/client';
import { useApiMutation, useReorder } from '../api/hooks';
import { SortableTable } from './SortableTable';
import { Card, ConfirmButton, ErrorMessage } from './ui';

const KEY = ['admin', 'quick-notes'];

/**
 * Feedback instructors can pick in the app instead of typing: on the scan screen (saved with every
 * grade until changed) and when adding a note to a grading.
 */
export function QuickNotesCard() {
  const notes = useQuery({
    queryKey: KEY,
    queryFn: () =>
      api.request<{ items: QuickNoteDto[] }>('/admin/quick-notes').then((r) => r.items),
  });
  const [text, setText] = useState('');
  const create = useApiMutation<void>(
    () => ({ path: '/admin/quick-notes', method: 'POST', body: { text } }),
    [KEY],
  );
  const reorder = useReorder(KEY, () => '/admin/quick-notes/order');
  return (
    <Card title="Quick notes">
      <p className="muted">
        Common feedback instructors can pick while grading instead of typing it out. Students see it
        on the technique they were graded on. Drag to change the order shown in the app.
      </p>
      <ErrorMessage error={notes.error ?? create.error ?? reorder.error} />
      <form
        className="btn-row"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate(undefined, { onSuccess: () => setText('') });
        }}
      >
        <input
          type="text"
          aria-label="New quick note"
          placeholder="e.g. Keep your elbows tight on the finish"
          required
          maxLength={QUICK_NOTE_MAX_LENGTH}
          value={text}
          onChange={(e) => setText(e.target.value)}
          style={{ maxWidth: 420 }}
        />
        <button className="btn primary" type="submit" disabled={create.isPending}>
          Add quick note
        </button>
      </form>
      <SortableTable
        groups={
          notes.data
            ? [
                {
                  id: 'all',
                  name: 'Quick notes',
                  rows: [...notes.data].sort((a, b) => a.order - b.order),
                  empty: 'No quick notes yet.',
                },
              ]
            : undefined
        }
        rowKey={(n) => n.id}
        rowLabel={(n) => n.text}
        onReorder={(_group, ids) => reorder.mutate({ groupId: 'all', ids })}
        columns={[
          { key: 'text', header: 'Note', render: (n) => <QuickNoteText note={n} /> },
          { key: 'actions', header: '', width: 100, render: (n) => <DeleteQuickNote note={n} /> },
        ]}
      />
    </Card>
  );
}

function QuickNoteText({ note }: { note: QuickNoteDto }) {
  const update = useApiMutation<string>(
    (text) => ({ path: `/admin/quick-notes/${note.id}`, method: 'PATCH', body: { text } }),
    [KEY],
  );
  return (
    <>
      <input
        type="text"
        aria-label={`Quick note: ${note.text}`}
        defaultValue={note.text}
        maxLength={QUICK_NOTE_MAX_LENGTH}
        required
        onBlur={(e) => {
          const text = e.target.value.trim();
          if (text && text !== note.text) update.mutate(text);
        }}
      />
      <ErrorMessage error={update.error} />
    </>
  );
}

function DeleteQuickNote({ note }: { note: QuickNoteDto }) {
  const remove = useApiMutation<void>(
    () => ({ path: `/admin/quick-notes/${note.id}`, method: 'DELETE' }),
    [KEY],
  );
  return (
    <ConfirmButton confirmLabel="Delete note?" onConfirm={() => remove.mutate()}>
      Delete
    </ConfirmButton>
  );
}
